import { NavMarker } from 'react-rhumb';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ApiError from '../../../../components/Errors/ApiError';
import { useTeamMembersCertificationsByStatus } from '../../myTeamQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import UserAvatarLink from '../common/UserAvatarLink';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useCertificationsByTeamActiveTableStore } from '../../store/myTeamTableStateStore';
import { useParams } from 'react-router-dom';
import { parseCertificationStatusFilter } from '../../utils/filterUtils';
import EmptyTable from '../common/EmptyTable';
import MyTeamTablePagination from '../common/TablePagination';
import { useTableSorting } from '../../hooks/tableHooks';
import CertificationLink from '../common/CertificationLink';
import { trackAwardLinkClick, trackDownloadCertificateClick } from '../../utils/trackingUtils';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import ViewAwardButton from '../common/ViewAwardButton';
import { Flex } from 'foundations-components/components/Flex';
import { isExpiringSoon } from '../../utils/dateTimeUtils';
import { useUserInfo } from '../../../accounts/accountsQueries';
import ExpiringSoonWarningIcon from './ExpiringSoonWarningIcon';
import { useTheme } from 'foundations-theming/utils';
import { useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CertificationsByTeamActiveTable = () => {
  const theme = useTheme();
  const {
    certificationStatus: certificationStatusParam
  } = useParams();
  const certificationsByTeamActiveTableStore = useCertificationsByTeamActiveTableStore();
  const {
    state: {
      search,
      teams,
      users,
      categories,
      tools,
      sortBy,
      sortOrder,
      page,
      pageSize
    },
    actions: {
      resetTableState,
      setSortBy,
      setSortOrder,
      setPage,
      setPageSize
    }
  } = certificationsByTeamActiveTableStore;
  const {
    isNotTablet
  } = useMediaQuery();
  const {
    userInfo
  } = useUserInfo();
  const currentUserId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id;
  const totalResultsRef = useRef(0);
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    teamMembersCertifications,
    teamMembersCertificationsLoading,
    teamMembersCertificationsError
  } = useTeamMembersCertificationsByStatus({
    certificationStatus: parseCertificationStatusFilter(certificationStatusParam),
    teamIds: teams,
    userIds: users,
    categories,
    tools,
    pageLength: pageSize,
    teamMemberNameFilter: search,
    sortField: sortBy,
    sortOrder: sortOrder,
    offset: {
      offsetPosition
    }
  });
  if ((teamMembersCertifications === null || teamMembersCertifications === void 0 ? void 0 : teamMembersCertifications.results.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "CERTIFICATIONS_BY_TEAM_TABLE_EMPTY",
      onResetClick: resetTableState
    });
  }
  if (teamMembersCertificationsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATIONS_BY_TEAM_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!teamMembersCertificationsLoading && teamMembersCertifications) {
    totalResultsRef.current = teamMembersCertifications.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [teamMembersCertificationsLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: 5,
      condensed: true,
      rows: pageSize
    }) : teamMembersCertifications && /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATIONS_BY_TEAM_TABLE_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "certifications-by-team-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '25%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '30%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('NAME'),
              sort: getSortDirectionForColumn('NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.name"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('CERTIFICATION_NAME'),
              sort: getSortDirectionForColumn('CERTIFICATION_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.certification"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('COMPLETED_AT'),
              sort: getSortDirectionForColumn('COMPLETED_AT'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.completed"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('EXPIRES_AT'),
              sort: getSortDirectionForColumn('EXPIRES_AT'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.expiration"
              })
            }), /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.awardLink"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: teamMembersCertifications.results.map((cert, index) => {
            const position = getPaginatedPosition(offsetPosition, index);
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "certifications-by-team-active-table-data-row",
              children: [/*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(UserAvatarLink, {
                  displayAvatar: isNotTablet,
                  position: position,
                  userEmail: cert.userEmail,
                  userId: cert.userId,
                  userName: cert.userName
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-certification-link",
                children: /*#__PURE__*/_jsx(CertificationLink, {
                  certificationIconUrl: cert.certificationIconUrl,
                  certificationId: cert.certificationId,
                  certificationName: cert.certificationName,
                  position: position
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-completed-at",
                children: cert.completedAt && /*#__PURE__*/_jsx(FormattedDateTime, {
                  format: "ll",
                  value: cert.completedAt
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-expires-at",
                children: cert.expiresAt ? /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  gap: "200",
                  children: [/*#__PURE__*/_jsx(FormattedDateTime, {
                    format: "ll",
                    value: cert.expiresAt
                  }), cert.userId === currentUserId && isExpiringSoon(cert.expiresAt) && /*#__PURE__*/_jsx(ExpiringSoonWarningIcon, {
                    cert: cert
                  })]
                }) : '--'
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-award-link",
                children: cert.grantedCredentialId && /*#__PURE__*/_jsx(ViewAwardButton, {
                  grantedCredentialId: cert.grantedCredentialId,
                  onClick: () => {
                    trackAwardLinkClick(cert.certificationId, cert.certificationName);
                  },
                  onDownloadCertificateClick: () => {
                    trackDownloadCertificateClick(cert.certificationId, cert.certificationName);
                  }
                })
              })]
            }, `${cert.userId}-${cert.certificationId}`);
          })
        })]
      })
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      page: page,
      pageSize: pageSize,
      totalResults: totalResultsRef.current
    })]
  });
};
export default CertificationsByTeamActiveTable;